import { saveManager } from './SaveManager';
import { bus, Events } from './EventBus';

export interface AchievementDef {
  id: string;
  name: string;
  desc: string;
  /** Skill achievements record the highest difficulty tier reached. */
  difficultyStars?: boolean;
}

export const ACHIEVEMENTS: AchievementDef[] = [
  { id: 'first_blood', name: 'First Blood', desc: 'Destroy your first Undermesh unit' },
  { id: 'hold_kappa', name: 'Outpost Kappa Held', desc: 'Complete the Kappa mission' },
  { id: 'perfect_kappa', name: 'Untouchable', desc: 'Finish a mission with full integrity' },
  { id: 'kill_100', name: 'Grinder', desc: 'Destroy 100 enemies', difficultyStars: true },
  { id: 'kill_500', name: 'Meshbreaker', desc: 'Destroy 500 enemies', difficultyStars: true },
  { id: 'art_50', name: 'Fire Mission', desc: 'Destroy 50 enemies with explosive damage', difficultyStars: true },
  { id: 'therm_50', name: 'Cauterized', desc: 'Destroy 50 enemies with thermal damage', difficultyStars: true },
  { id: 'wave_10', name: 'Full Course', desc: 'Reach wave 10', difficultyStars: true },
  { id: 'wave_20', name: 'Endurance', desc: 'Survive to wave 20 (survival)', difficultyStars: true },
  { id: 'boss_slayer', name: 'Colossus Down', desc: 'Defeat a boss' },
  { id: 'ability_10', name: 'Hands On', desc: 'Use battlefield abilities 10 times' },
  { id: 'nightmare', name: 'No Exit', desc: 'Clear a Nightmare mission' },
  { id: 'kill_1000', name: 'Attrition Engine', desc: 'Destroy 1,000 enemies', difficultyStars: true },
  { id: 'kill_2500', name: 'Worldbreaker', desc: 'Destroy 2,500 enemies', difficultyStars: true },
  { id: 'art_250', name: 'Thunder Run', desc: 'Destroy 250 enemies with explosive damage', difficultyStars: true },
  { id: 'therm_250', name: 'White Hot', desc: 'Destroy 250 enemies with thermal damage', difficultyStars: true },
  { id: 'energy_250', name: 'Grid Collapse', desc: 'Destroy 250 enemies with energy damage', difficultyStars: true },
  { id: 'ballistic_250', name: 'Steel Discipline', desc: 'Destroy 250 enemies with ballistic damage', difficultyStars: true },
  { id: 'wave_30', name: 'Thirty Deep', desc: 'Reach wave 30', difficultyStars: true },
  { id: 'centurion_survivor', name: 'Centurion Survivor', desc: 'Survive 100 waves in Survival Mode', difficultyStars: true },
  { id: 'boss_5', name: 'Apex Hunter', desc: 'Defeat 5 bosses' },
  { id: 'boss_15', name: 'Titanfall', desc: 'Defeat 15 bosses' },
  { id: 'ability_50', name: 'Tactical Rhythm', desc: 'Use battlefield abilities 50 times' },
  { id: 'ability_100', name: 'Full Spectrum', desc: 'Use battlefield abilities 100 times' },
  { id: 'perfect_5', name: 'Immovable', desc: 'Finish 5 missions with full integrity' },
  { id: 'mission_3', name: 'Campaigner', desc: 'Complete 3 campaign missions' },
  { id: 'mission_6', name: 'Theatre Commander', desc: 'Complete 6 campaign missions' },
  { id: 'mission_11', name: 'All Fronts', desc: 'Complete every campaign mission' },
  { id: 'rank_5', name: 'Field Officer', desc: 'Reach command rank 5' },
  { id: 'rank_10', name: 'Bastion Marshal', desc: 'Reach command rank 10' },
  { id: 'rank_20', name: 'Apogee', desc: 'Reach command rank 20' },
  { id: 'research_5', name: 'Applied Science', desc: 'Unlock 5 research projects' },
  { id: 'research_15', name: 'Black Budget', desc: 'Unlock 15 research projects' },
];

export class AchievementManager {
  private dirty = false;

  private difficultyStars(difficulty: string): number {
    return difficulty === 'nightmare' ? 3 : difficulty === 'hard' || difficulty === 'medium' ? 2 : 1;
  }

  private noteSkillAchievement(id: string, difficulty: string): void {
    const def = ACHIEVEMENTS.find((achievement) => achievement.id === id);
    if (!def?.difficultyStars) return;
    const next = this.difficultyStars(difficulty);
    const current = saveManager.data.achievementStars[id] ?? 0;
    if (next > current) {
      saveManager.data.achievementStars[id] = next;
      this.dirty = true;
    }
    this.unlock(id);
  }

  unlock(id: string): boolean {
    if (saveManager.data.achievements.includes(id)) return false;
    const def = ACHIEVEMENTS.find((a) => a.id === id);
    if (!def) return false;
    saveManager.data.achievements.push(id);
    this.dirty = true;
    saveManager.save();
    bus.emit('achievement', def);
    return true;
  }

  isUnlocked(id: string): boolean {
    return saveManager.data.achievements.includes(id);
  }

  noteKill(damageType: string, difficulty = 'normal'): void {
    const s = saveManager.data.stats;
    s.kills++;
    if (damageType === 'EXPLOSIVE') s.artilleryKills++;
    if (damageType === 'THERMAL') s.thermalKills++;
    if (damageType === 'ENERGY') s.energyKills++;
    if (damageType === 'BALLISTIC') s.ballisticKills++;
    this.dirty = true;
    if (s.kills === 1) this.unlock('first_blood');
    if (s.kills >= 100) this.noteSkillAchievement('kill_100', difficulty);
    if (s.kills >= 500) this.noteSkillAchievement('kill_500', difficulty);
    if (s.kills >= 1000) this.noteSkillAchievement('kill_1000', difficulty);
    if (s.kills >= 2500) this.noteSkillAchievement('kill_2500', difficulty);
    if (s.artilleryKills >= 50) this.noteSkillAchievement('art_50', difficulty);
    if (s.thermalKills >= 50) this.noteSkillAchievement('therm_50', difficulty);
    if (s.artilleryKills >= 250) this.noteSkillAchievement('art_250', difficulty);
    if (s.thermalKills >= 250) this.noteSkillAchievement('therm_250', difficulty);
    if (s.energyKills >= 250) this.noteSkillAchievement('energy_250', difficulty);
    if (s.ballisticKills >= 250) this.noteSkillAchievement('ballistic_250', difficulty);
    if (this.dirty) {
      saveManager.save();
      this.dirty = false;
    }
  }

  noteWave(n: number, survival: boolean, difficulty = 'normal'): void {
    saveManager.data.stats.maxWave = Math.max(saveManager.data.stats.maxWave, n);
    if (n >= 10) this.noteSkillAchievement('wave_10', difficulty);
    if (survival && n >= 20) this.noteSkillAchievement('wave_20', difficulty);
    if (n >= 30) this.noteSkillAchievement('wave_30', difficulty);
    saveManager.save();
    this.dirty = false;
  }

  noteSurvivalWaveCleared(n: number, difficulty = 'normal'): void {
    if (n >= 100) this.noteSkillAchievement('centurion_survivor', difficulty);
    if (this.dirty) {
      saveManager.save();
      this.dirty = false;
    }
  }

  noteBossKill(): void {
    saveManager.data.stats.bossesDefeated++;
    saveManager.save();
    this.unlock('boss_slayer');
    if (saveManager.data.stats.bossesDefeated >= 5) this.unlock('boss_5');
    if (saveManager.data.stats.bossesDefeated >= 15) this.unlock('boss_15');
  }

  noteAbility(): void {
    saveManager.data.stats.abilitiesUsed++;
    saveManager.save();
    if (saveManager.data.stats.abilitiesUsed >= 10) this.unlock('ability_10');
    if (saveManager.data.stats.abilitiesUsed >= 50) this.unlock('ability_50');
    if (saveManager.data.stats.abilitiesUsed >= 100) this.unlock('ability_100');
  }

  noteMissionComplete(missionId: string, integrity: number, maxIntegrity: number, difficulty: string): void {
    if (missionId === 'kappa') this.unlock('hold_kappa');
    if (integrity >= maxIntegrity) {
      saveManager.data.stats.perfectMissions++;
      this.unlock('perfect_kappa');
      if (saveManager.data.stats.perfectMissions >= 5) this.unlock('perfect_5');
    }
    if (difficulty === 'nightmare') this.unlock('nightmare');
    const completed = saveManager.data.missionsComplete.length;
    if (completed >= 3) this.unlock('mission_3');
    if (completed >= 6) this.unlock('mission_6');
    if (completed >= 11) this.unlock('mission_11');
    saveManager.save();
  }

  noteRank(): void {
    const rank = saveManager.data.rank;
    if (rank >= 5) this.unlock('rank_5');
    if (rank >= 10) this.unlock('rank_10');
    if (rank >= 20) this.unlock('rank_20');
  }

  noteResearch(): void {
    const count = saveManager.data.research.length;
    if (count >= 5) this.unlock('research_5');
    if (count >= 15) this.unlock('research_15');
  }
}

export const achievements = new AchievementManager();
